import { isUuid } from '@/lib/validation';
import type { ServiceClient } from '../http/client';
import { ApiError } from '../http/errors';

/**
 * 장기(월) 가격 — `…/long-term-pricing?price_basis=MONTH`. 정본:
 * core-admin-resource-fixes(PR182 head) app/modules/booking/interfaces/http/long_term_pricing.py.
 * 월 기준은 항상 price_basis=MONTH 로 명시한다(생략하면 일 기준). 편집용 CAS 는 서버가 준 값을 그대로 돌려보내고
 * 화면에서 만들거나 중기 CAS 로 대신하지 않는다. 금액 계산·상속 판단은 하지 않는다.
 */
export type LongPricingKind = 'room-types' | 'rooms' | 'units';
export interface LongPricingTarget {
  kind: LongPricingKind;
  id: string;
}
export interface LongPolicy {
  id: string;
  target_kind: 'TYPE' | 'ROOM' | 'UNIT';
  room_type_id: string | null;
  room_id: string | null;
  unit_id: string | null;
  price_basis: string;
  regular_amount_minor: number | null;
  deposit_amount: number | null;
  cleaning_fee: number | null;
  revision: number;
  deleted_at: string | null;
}
export type LongRuleType = 'AMOUNT_DISCOUNT' | 'RATE_DISCOUNT';
export interface LongRule {
  id: string;
  price_policy_id: string;
  term_tier_id: string | null;
  applies_to: 'NEW' | 'EXTENSION';
  booking_from: string | null;
  booking_until: string | null;
  calculation_type: LongRuleType | 'FIXED_PRICE';
  /** 서버 NUMERIC(25,6)의 JSON number. 화면은 보여 주기만 하고 계산하지 않는다. */
  calculation_value: number;
  revision: number;
  deleted_at: string | null;
}
export interface LongTier {
  id: string;
  code: 'S' | 'M' | 'L';
  duration_unit: 'MONTH' | 'DAY';
  min_duration: number;
  max_duration: number;
  revision: number;
  deleted_at: string | null;
}
export interface LongPricingRead {
  /** 서버가 준 전체 비교값. 수정하지 않고 그대로 명령에 싣는다. */
  cas: Record<string, unknown>;
  policies: LongPolicy[];
  rules: LongRule[];
  tiers: LongTier[];
}
export interface LongPricingValues {
  regular_amount_minor: number | null;
  deposit_amount: number | null;
  cleaning_fee: number | null;
}
export interface LongPricingPut {
  command_id: string;
  reason: string;
  expected: Record<string, unknown>;
  values: LongPricingValues;
}
export interface FieldSource {
  value: number | null;
  source_policy_id: string | null;
  revision: number | null;
  reason: 'TYPE_POLICY_ABSENT' | 'LEGACY_UNKNOWN' | null;
}
export interface LongMonthEvaluation {
  monthly_regular_amount_minor: number;
  monthly_effective_amount_minor: number;
  complete_months: number;
  remaining_days: number;
  next_contract_month_days: number;
  accommodation_amount: number;
  deposit_amount: number;
  cleaning_fee: number;
  total: number;
  period: { first_date: string; end_date: string };
  field_sources: {
    regular_amount_minor: FieldSource;
    deposit_amount: FieldSource;
    cleaning_fee: FieldSource;
  };
}
export interface LongMonthEvaluate {
  applies_to: 'NEW' | 'EXTENSION';
  first_date: string;
  term_unit: 'MONTH' | 'DAY';
  term_value: number;
}

const record = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === 'object' && !Array.isArray(v);
const exact = (v: Record<string, unknown>, keys: string[]) =>
  Object.keys(v).length === keys.length && keys.every((k) => Object.hasOwn(v, k));
const count = (v: unknown): v is number => Number.isSafeInteger(v) && Number(v) >= 0;
const positive = (v: unknown): v is number => count(v) && v > 0;
const nullable = <T>(v: unknown, ok: (x: unknown) => x is T): v is T | null => v === null || ok(v);
const str = (v: unknown): v is string => typeof v === 'string';
const invalid = () =>
  new ApiError({
    kind: 'invalid-response',
    service: 'core',
    message: '장기 월 가격 응답을 확인하지 못했습니다.',
  });
const path = (t: LongPricingTarget) => {
  if (!['room-types', 'rooms', 'units'].includes(t.kind) || !isUuid(t.id)) throw invalid();
  return `admin/${t.kind}/${t.id}/long-term-pricing`;
};
const POLICY_KEYS = [
  'id',
  'property_id',
  'pricing_policy_code',
  'target_kind',
  'room_type_id',
  'room_id',
  'unit_id',
  'currency',
  'price_basis',
  'regular_amount_minor',
  'deposit_amount',
  'cleaning_fee',
  'value_semantics',
  'revision',
  'created_at',
  'updated_at',
  'deleted_at',
];
function policy(v: unknown): LongPolicy {
  if (
    !record(v) ||
    !exact(v, POLICY_KEYS) ||
    !isUuid(v.id) ||
    !['TYPE', 'ROOM', 'UNIT'].includes(String(v.target_kind)) ||
    !nullable(v.room_type_id, isUuid) ||
    !nullable(v.room_id, isUuid) ||
    !nullable(v.unit_id, isUuid) ||
    !str(v.price_basis) ||
    !nullable(v.regular_amount_minor, count) ||
    !nullable(v.deposit_amount, count) ||
    !nullable(v.cleaning_fee, count) ||
    !positive(v.revision) ||
    !nullable(v.deleted_at, str)
  )
    throw invalid();
  return v as unknown as LongPolicy;
}
const RULE_KEYS = [
  'id',
  'price_policy_id',
  'term_tier_id',
  'applies_to',
  'booking_from',
  'booking_until',
  'calculation_type',
  'calculation_value',
  'revision',
  'created_at',
  'updated_at',
  'deleted_at',
];
const TIER_KEYS = [
  'id',
  'property_id',
  'code',
  'duration_unit',
  'min_duration',
  'max_duration',
  'revision',
  'created_at',
  'updated_at',
  'deleted_at',
];
function rule(v: unknown): LongRule {
  if (
    !record(v) ||
    !exact(v, RULE_KEYS) ||
    !isUuid(v.id) ||
    !isUuid(v.price_policy_id) ||
    !nullable(v.term_tier_id, isUuid) ||
    !['NEW', 'EXTENSION'].includes(String(v.applies_to)) ||
    !nullable(v.booking_from, str) ||
    !nullable(v.booking_until, str) ||
    !['AMOUNT_DISCOUNT', 'RATE_DISCOUNT', 'FIXED_PRICE'].includes(String(v.calculation_type)) ||
    typeof v.calculation_value !== 'number' ||
    !(v.calculation_value > 0) ||
    !positive(v.revision) ||
    !nullable(v.deleted_at, str)
  )
    throw invalid();
  return v as unknown as LongRule;
}
export function tier(v: unknown): LongTier {
  if (
    !record(v) ||
    !exact(v, TIER_KEYS) ||
    !isUuid(v.id) ||
    !['S', 'M', 'L'].includes(String(v.code)) ||
    !['MONTH', 'DAY'].includes(String(v.duration_unit)) ||
    !positive(v.min_duration) ||
    !positive(v.max_duration) ||
    !positive(v.revision) ||
    !nullable(v.deleted_at, str)
  )
    throw invalid();
  return v as unknown as LongTier;
}
function state(v: unknown): Omit<LongPricingRead, 'cas'> {
  if (
    !record(v) ||
    !exact(v, [
      'booking_room_sales',
      'price_policies',
      'price_policy_rules',
      'contract_term_tiers',
    ]) ||
    !Object.values(v).every(Array.isArray)
  )
    throw invalid();
  return {
    policies: (v.price_policies as unknown[]).map(policy),
    rules: (v.price_policy_rules as unknown[]).map(rule),
    tiers: (v.contract_term_tiers as unknown[]).map(tier),
  };
}
function cas(v: unknown): Record<string, unknown> {
  if (
    !record(v) ||
    !exact(v, ['property_id', 'target', 'configurations', 'policies', 'tiers', 'rule_revisions']) ||
    !isUuid(v.property_id) ||
    !record(v.target) ||
    !exact(v.target, [
      'kind',
      'property_id',
      'type_id',
      'room_id',
      'structure_revision',
      'version',
      ...(v.target.kind === 'UNIT' ? ['unit_id'] : []),
    ]) ||
    v.target.property_id !== v.property_id ||
    !positive(v.target.version) ||
    !positive(v.target.structure_revision) ||
    !(v.target.kind === 'PROPERTY'
      ? v.target.type_id === null && v.target.room_id === null
      : isUuid(v.target.type_id) &&
        (v.target.kind === 'TYPE'
          ? v.target.room_id === null
          : ['ROOM', 'UNIT'].includes(String(v.target.kind)) &&
            isUuid(v.target.room_id) &&
            (v.target.kind !== 'UNIT' || isUuid(v.target.unit_id)))) ||
    !record(v.configurations) ||
    !record(v.policies) ||
    !record(v.tiers) ||
    !record(v.rule_revisions)
  )
    throw invalid();
  return v;
}

export async function readLongMonthPricing(
  client: ServiceClient,
  target: LongPricingTarget,
  signal?: AbortSignal,
): Promise<LongPricingRead> {
  const v = await client.get<unknown>(path(target), {
    params: { price_basis: 'MONTH', view: 'edit' },
    cache: 'no-store',
    signal,
  });
  if (!record(v) || !exact(v, ['cas', 'state'])) throw invalid();
  const checked = cas(v.cas);
  const targetKey = record(checked.target) ? checked.target : {};
  const expectedKind = { 'room-types': 'TYPE', rooms: 'ROOM', units: 'UNIT' }[target.kind];
  if (
    targetKey.kind !== expectedKind ||
    (target.kind === 'units' && targetKey.unit_id !== target.id) ||
    (target.kind === 'rooms' && targetKey.room_id !== target.id) ||
    (target.kind === 'room-types' && targetKey.type_id !== target.id)
  )
    throw invalid();
  return { cas: checked, ...state(v.state) };
}

/** 이 대상이 직접 가진 현재(삭제되지 않은) 월 정책. 없으면 아직 직접 설정한 값이 없다. */
export function ownLongPolicy(read: LongPricingRead, target: LongPricingTarget) {
  const kind = { 'room-types': 'TYPE', rooms: 'ROOM', units: 'UNIT' }[target.kind];
  return (
    read.policies.find(
      (p) =>
        p.deleted_at === null &&
        p.price_basis === 'MONTH' &&
        p.target_kind === kind &&
        (kind === 'TYPE' ? p.room_type_id : kind === 'ROOM' ? p.room_id : p.unit_id) === target.id,
    ) ?? null
  );
}

interface CommandBase {
  command_id: string;
  reason: string;
  expected: Record<string, unknown>;
}
export interface LongRuleValues {
  term_tier_id: string | null;
  applies_to: 'NEW' | 'EXTENSION';
  /** 접수 시각 하한(포함). aware ISO 시각 문자열 또는 null(제한 없음). */
  booking_from: string | null;
  /** 접수 시각 상한(미포함). aware ISO 시각 문자열 또는 null(제한 없음). */
  booking_until: string | null;
  calculation_type: LongRuleType;
  /** 정액은 JSON 정수, 비율은 소수 6자리 이내 십진 문자열. */
  calculation_value: string | number;
}
/** 한 번의 쓰기. 대상 경로·본문을 함께 담아 같은 요청을 그대로 재전송할 수 있다. */
export type LongMonthCommand =
  | { op: 'policy'; body: LongPricingPut }
  | { op: 'retire-policy'; policyId: string; body: CommandBase }
  | { op: 'rule-create'; policyId: string; body: CommandBase & { rule: LongRuleValues } }
  | {
      op: 'rule-put';
      policyId: string;
      ruleId: string;
      body: CommandBase & { rule: LongRuleValues };
    }
  | { op: 'rule-retire'; policyId: string; ruleId: string; body: CommandBase };

function checkCommandResult(v: unknown, commandId: string) {
  if (
    !record(v) ||
    !exact(v, [
      'schema_version',
      'command_id',
      'outcome',
      'first_occurred_at',
      'result_revision',
      'state',
      'transitions',
    ]) ||
    v.schema_version !== 1 ||
    v.command_id !== commandId ||
    !['CHANGED', 'NO_CHANGE'].includes(String(v.outcome)) ||
    !count(v.result_revision) ||
    !Array.isArray(v.transitions)
  )
    throw invalid();
  state(v.state);
}
const MONTH = { price_basis: 'MONTH' };
const ruleBase = (policyId: string) => {
  if (!isUuid(policyId)) throw invalid();
  return `admin/price-policies/${policyId}/long-term-pricing/rules`;
};
const ruleUuid = (id: string) => {
  if (!isUuid(id)) throw invalid();
  return id;
};

export async function sendLongMonthCommand(
  client: ServiceClient,
  target: LongPricingTarget,
  command: LongMonthCommand,
): Promise<void> {
  let v: unknown;
  switch (command.op) {
    case 'policy':
      v = await client.put<unknown>(path(target), { params: MONTH, json: command.body });
      break;
    case 'retire-policy':
      // 정책 전체를 해제한다. 항목 하나를 비우는 PUT과 다르다.
      v = await client.delete<unknown>(path(target), { params: MONTH, json: command.body });
      break;
    case 'rule-create':
      v = await client.post<unknown>(ruleBase(command.policyId), {
        params: MONTH,
        json: command.body,
      });
      break;
    case 'rule-put':
      v = await client.put<unknown>(`${ruleBase(command.policyId)}/${ruleUuid(command.ruleId)}`, {
        params: MONTH,
        json: command.body,
      });
      break;
    case 'rule-retire':
      v = await client.delete<unknown>(
        `${ruleBase(command.policyId)}/${ruleUuid(command.ruleId)}`,
        { params: MONTH, json: command.body },
      );
      break;
  }
  checkCommandResult(v, command.body.command_id);
}

export function putLongMonthPricing(
  client: ServiceClient,
  target: LongPricingTarget,
  body: LongPricingPut,
): Promise<void> {
  return sendLongMonthCommand(client, target, { op: 'policy', body });
}

export interface ContractTiersRead {
  property_id: string;
  tiers: LongTier[];
  cas: Record<string, unknown>;
  referencing_rules: LongRule[];
}
export interface ContractTiersPut extends CommandBase {
  tiers: Pick<LongTier, 'code' | 'duration_unit' | 'min_duration' | 'max_duration'>[];
  retire_rule_ids: string[];
}
/** 지점 계약 구간. 구간 경로에는 price_basis를 붙이지 않는다(MONTH는 구간 쓰기를 거절한다). */
export async function readContractTiers(
  client: ServiceClient,
  propertyId: string,
  signal?: AbortSignal,
): Promise<ContractTiersRead> {
  if (!isUuid(propertyId)) throw invalid();
  const v = await client.get<unknown>(
    `admin/properties/${propertyId}/long-term-pricing/contract-term-tiers`,
    { params: { view: 'edit' }, cache: 'no-store', signal },
  );
  if (!record(v) || !exact(v, ['cas', 'state'])) throw invalid();
  const checked = cas(v.cas);
  const target = checked.target;
  if (
    checked.property_id !== propertyId ||
    !record(target) ||
    target.kind !== 'PROPERTY' ||
    target.property_id !== propertyId
  )
    throw invalid();
  const current = state(v.state);
  return {
    property_id: propertyId,
    tiers: current.tiers,
    cas: checked,
    referencing_rules: current.rules,
  };
}
export async function putContractTiers(
  client: ServiceClient,
  propertyId: string,
  body: ContractTiersPut,
): Promise<void> {
  if (!isUuid(propertyId)) throw invalid();
  const v = await client.put<unknown>(
    `admin/properties/${propertyId}/long-term-pricing/contract-term-tiers`,
    { json: body },
  );
  checkCommandResult(v, body.command_id);
}

const source = (v: unknown): v is FieldSource =>
  record(v) &&
  exact(v, ['value', 'source_policy_id', 'revision', 'reason']) &&
  nullable(v.value, count) &&
  nullable(v.source_policy_id, isUuid) &&
  nullable(v.revision, positive) &&
  (v.reason === null ||
    (typeof v.reason === 'string' && ['TYPE_POLICY_ABSENT', 'LEGACY_UNKNOWN'].includes(v.reason)));

export async function evaluateLongMonth(
  client: ServiceClient,
  target: { kind: 'rooms' | 'units'; id: string },
  body: LongMonthEvaluate,
  signal?: AbortSignal,
): Promise<LongMonthEvaluation> {
  const v = await client.post<unknown>(`${path(target)}/evaluate`, {
    params: { price_basis: 'MONTH' },
    json: body,
    signal,
  });
  if (
    !record(v) ||
    !exact(v, [
      'price_basis',
      'monthly_regular_amount_minor',
      'monthly_effective_amount_minor',
      'complete_months',
      'remaining_days',
      'next_contract_month_days',
      'accommodation_amount',
      'deposit_amount',
      'cleaning_fee',
      'total',
      'period',
      'tier_snapshot',
      'selected_rule',
      'field_sources',
      'cas',
      'received_at',
      'calculation_version',
    ]) ||
    v.price_basis !== 'MONTH' ||
    v.calculation_version !== 'LONG_MONTH_V1' ||
    !positive(v.monthly_regular_amount_minor) ||
    !positive(v.monthly_effective_amount_minor) ||
    !positive(v.complete_months) ||
    !count(v.remaining_days) ||
    !positive(v.next_contract_month_days) ||
    !positive(v.accommodation_amount) ||
    !count(v.deposit_amount) ||
    !count(v.cleaning_fee) ||
    !positive(v.total) ||
    !record(v.period) ||
    !exact(v.period, ['first_date', 'end_date']) ||
    !record(v.field_sources) ||
    !exact(v.field_sources, ['regular_amount_minor', 'deposit_amount', 'cleaning_fee']) ||
    !source(v.field_sources.regular_amount_minor) ||
    !source(v.field_sources.deposit_amount) ||
    !source(v.field_sources.cleaning_fee)
  )
    throw invalid();
  const evaluationCas = cas(v.cas);
  const evaluationTarget = evaluationCas.target as Record<string, unknown>;
  if (
    evaluationTarget.kind !== (target.kind === 'units' ? 'UNIT' : 'ROOM') ||
    evaluationTarget[target.kind === 'units' ? 'unit_id' : 'room_id'] !== target.id
  )
    throw invalid();
  return v as unknown as LongMonthEvaluation;
}
